import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  FileText,
  FileCheck,
  Building2,
  Shield,
  Settings,
  FileSearch,
  CheckSquare,
  DollarSign,
  Activity,
  ChevronRight,
  Calculator,
  Package,
  FolderKanban,
  Zap,
  BarChart2,
  Handshake,
  ClipboardList,
  CreditCard,
  ShieldCheck,
  Lock,
  UsersRound,
  KeyRound,
} from "lucide-react";
import logoMark from "@/assets/logo-mark.png";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { LucideIcon } from "lucide-react";
import { useAccessControl } from "@/contexts/AccessControlContext";
import { Modules, Features, Permissions } from "@prolarus/shared";

interface NavChildItem {
  title: string;
  url: string;
  icon?: LucideIcon;
  moduleCode?: string;
  permissionCode?: string | string[];
  featureCode?: string;
}

interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  moduleCode?: string;
  permissionCode?: string | string[];
  featureCode?: string;
  children?: NavChildItem[];
}

const mainNavItems: NavItem[] = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
    children: [
      { title: "AP Velocity", url: "/dashboard/ap-speed", icon: Zap },
      { title: "Contract Renewals", url: "/dashboard/contract-renewals", icon: Handshake },
      { title: "Compliance Insight", url: "/dashboard", icon: ShieldCheck },
      { title: "Model Performance", url: "/dashboard/model-performance", icon: BarChart2 },
      { title: "Supplier Report Cards", url: "/dashboard/supplier-report-cards", icon: ClipboardList },
      { title: "Payment Accuracy", url: "/dashboard/payment-accuracy", icon: CreditCard },
    ],
  },
  { title: "Suppliers", url: "/vendors", icon: Users },
  { title: "Projects", url: "/projects", icon: FolderKanban },
{ 
    title: "Price Books", 
    url: "/price-books", 
    icon: BookOpen,
    children: [
      { title: "Pricing", url: "/price-books", icon: DollarSign },
      { title: "Catalog", url: "/price-books/catalog", icon: Package },
    ]
  },
  {
    title: "Supplier Quotes",
    url: "/quotes",
    icon: Calculator,
    moduleCode: Modules.QUOTING,
    permissionCode: [
      Permissions.quote.create.code,
      Permissions.quote.read.code,
      Permissions.quote.update.code,
      Permissions.quote.delete.code,
    ],
  },
  {
    title: "Invoices",
    url: "/invoices",
    icon: FileText,
    moduleCode: Modules.COMPLIANCE,
    permissionCode: [
      Permissions.invoice.create.code,
      Permissions.invoice.read.code,
      Permissions.invoice.update.code,
      Permissions.invoice.delete.code,
    ],
  },
  { title: "Field Tickets", url: "/field-tickets", icon: FileCheck },
];

const settingsNavItems: NavItem[] = [
  { title: "Company Profile", url: "/company-profile", icon: Building2 },
  {
    title: "Security",
    url: "/security/users",
    icon: Lock,
    children: [
      { title: "Users", url: "/security/users", icon: Users },
      { title: "Groups", url: "/security/groups", icon: UsersRound },
      { title: "Permissions", url: "/security/permissions", icon: KeyRound },
    ],
  },
  { title: "Configuration", url: "/configuration", icon: Settings },
  { title: "Usage", url: "/usage", icon: Activity },
  { title: "Privacy & Compliance", url: "/privacy", icon: Shield },
];

const toolsNavItems: NavItem[] = [
  { title: "Extract Invoice", url: "/tools/extract-invoice", icon: FileSearch, moduleCode: Modules.COMPLIANCE, permissionCode: Permissions.invoice.upload.code },
  { title: "Validate Invoice", url: "/tools/validate-invoice", icon: CheckSquare, moduleCode: Modules.COMPLIANCE, permissionCode: Permissions.invoice.upload.code },
  { title: "Early Pay", url: "/tools/early-pay", icon: DollarSign, featureCode: Features.COMPLIANCE.EARLY_PAY },
];

export function AppSidebar() {
  const location = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { isLoading: accessLoading, canAccessModule, hasFeature, hasPermission } = useAccessControl();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');
  const isChildActive = (item: NavItem) =>
    item.children?.some(child => location.pathname === child.url || location.pathname.startsWith(child.url + '/'));
  const isParentOrChildActive = (item: NavItem) =>
    isActive(item.url) || isChildActive(item);

  const isItemAccessible = (item: { moduleCode?: string; permissionCode?: string | string[]; featureCode?: string }): boolean => {
    if (item.moduleCode && !canAccessModule(item.moduleCode)) return false;
    if (item.featureCode && !hasFeature(item.featureCode)) return false;
    if (item.permissionCode) {
      const codes = Array.isArray(item.permissionCode) ? item.permissionCode : [item.permissionCode];
      if (!codes.some(code => hasPermission(code))) return false;
    }
    return true;
  };

  const filterNavItems = (items: NavItem[]): NavItem[] => {
    if (accessLoading) return items;
    return items
      .map(item => {
        const filteredChildren = item.children?.filter(child => isItemAccessible(child));
        if (item.children && (!filteredChildren || filteredChildren.length === 0)) return null;
        if (!isItemAccessible(item)) return null;
        return { ...item, children: filteredChildren };
      })
      .filter((item): item is NavItem => item !== null);
  };

  const filteredMainNav = filterNavItems(mainNavItems);
  const filteredToolsNav = filterNavItems(toolsNavItems);
  const filteredSettingsNav = filterNavItems(settingsNavItems);

  const renderNavItem = (item: NavItem) => {
    if (item.children) {
      // Collapsed mode: Use dropdown menu for sub-items
      if (isCollapsed) {
        return (
          <SidebarMenuItem key={item.url}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton 
                  tooltip={item.title}
                  isActive={isParentOrChildActive(item)}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.title}</span>
                  <ChevronRight className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="start" className="min-w-[150px]">
                {item.children.map((child) => (
                  <DropdownMenuItem key={child.url} asChild>
                    <NavLink 
                      to={child.url} 
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      {child.icon && <child.icon className="w-4 h-4" />}
                      <span>{child.title}</span>
                    </NavLink>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        );
      }

      // Expanded mode: Use collapsible for inline sub-menu
      return (
        <Collapsible
          key={item.url}
          asChild
          defaultOpen={isParentOrChildActive(item)}
          className="group/collapsible"
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton 
                tooltip={item.title}
                isActive={isParentOrChildActive(item)}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.title}</span>
                <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {item.children.map((child) => (
                  <SidebarMenuSubItem key={child.url}>
                    <SidebarMenuSubButton asChild isActive={
                      location.pathname === child.url || location.pathname.startsWith(child.url + '/')
                    }>
                      <NavLink to={child.url}>
                        {child.icon && <child.icon className="w-4 h-4" />}
                        <span>{child.title}</span>
                      </NavLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    return (
      <SidebarMenuItem key={item.url}>
        <SidebarMenuButton asChild tooltip={item.title} isActive={isActive(item.url)}>
          <NavLink to={item.url}>
            <item.icon className="w-5 h-5" />
            <span>{item.title}</span>
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };



  return (
    <Sidebar collapsible="icon">
      {/* Logo */}
      <SidebarHeader className="border-b border-sidebar-border">
        <NavLink 
          to="/dashboard" 
          className="flex items-center gap-3 px-2 py-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
        >
          <img src={logoMark} alt="Prolarus" className="w-8 h-8 rounded-lg shrink-0" />
          {!isCollapsed && (
            <span className="text-xl font-semibold text-sidebar-accent-foreground">Prolarus</span>
          )}
        </NavLink>
      </SidebarHeader>

      {/* Main Navigation */}
      <SidebarContent>
        {filteredMainNav.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Main</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredMainNav.map(renderNavItem)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {filteredToolsNav.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Tools</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredToolsNav.map(renderNavItem)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {filteredSettingsNav.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Settings</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredSettingsNav.map(renderNavItem)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
